import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { calculateColumnValueFilterPosition } from '../utils/columnValueFilterPosition'
import { normalizeValueFilterSearch } from '../utils/valueFilter'

const positionsMatch = (current, next) =>
  current.placement === next.placement &&
  Object.keys(next.style).every((key) => current.style[key] === next.style[key])

const ColumnValueFilter = ({
  label,
  values = [],
  options = [],
  onApply,
  onClose,
  anchor,
  isLoading = false
}) => {
  const [query, setQuery] = useState('')
  const [draftValues, setDraftValues] = useState(values)
  const popupRef = useRef(null)
  const inputRef = useRef(null)
  const [position, setPosition] = useState({
    placement: 'bottom',
    style: {}
  })

  useLayoutEffect(() => {
    if (!anchor) return undefined
    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect()
      const popupHeight =
        popupRef.current?.scrollHeight ||
        popupRef.current?.getBoundingClientRect().height ||
        0
      const nextPosition = calculateColumnValueFilterPosition({
        anchorRect: rect,
        popupHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      })
      setPosition((current) =>
        positionsMatch(current, nextPosition) ? current : nextPosition
      )
    }
    updatePosition()
    // Filtering can change the table layout without resizing the icon. Queue
    // one more measurement after the new rows/header have been painted.
    const frame = requestAnimationFrame(updatePosition)
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(anchor)
    if (popupRef.current) resizeObserver.observe(popupRef.current)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchor, values, options])

  useEffect(() => {
    inputRef.current?.focus()
    const handleOutside = (event) => {
      if (isLoading) return
      if (
        !popupRef.current?.contains(event.target) &&
        !anchor?.contains(event.target)
      ) {
        onClose()
      }
    }
    const handleKeyDown = (event) => {
      if (isLoading) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [anchor, isLoading, onClose])

  const normalizedQuery = normalizeValueFilterSearch(query)
  const filteredOptions = options.filter((option) =>
    normalizeValueFilterSearch(option.label ?? option.value).includes(normalizedQuery)
  )
  const selected = new Set(draftValues)
  const isAllSelected =
    options.length > 0 && options.every((option) => selected.has(option.value))
  const isAllDeselected = selected.size === 0

  const toggleValue = (value) => {
    const next = new Set(selected)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setDraftValues(Array.from(next))
  }

  const popup = (
    <div
      ref={popupRef}
      className='column-value-filter-popup'
      style={position.style}
      data-placement={position.placement}
      role='dialog'
      aria-label={`${label}フィルター`}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className='column-value-filter-title'>{label}</div>
      <input
        ref={inputRef}
        className='column-value-filter-search'
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder='検索'
        aria-label={`${label}を検索`}
        disabled={isLoading}
      />
      <div className='column-value-filter-actions'>
        <button
          type='button'
          className={isAllSelected ? 'active' : ''}
          aria-pressed={isAllSelected}
          disabled={isLoading}
          onClick={() => setDraftValues(options.map((o) => o.value))}
        >
          すべて選択
        </button>
        <button
          type='button'
          className={isAllDeselected ? 'active' : ''}
          aria-pressed={isAllDeselected}
          disabled={isLoading}
          onClick={() => setDraftValues([])}
        >
          選択を解除
        </button>
        <span>{selected.size}件選択</span>
      </div>
      <div className='column-value-filter-options'>
        {filteredOptions.length === 0 ? (
          <div className='column-value-filter-empty'>
            該当する値がありません
          </div>
        ) : (
          filteredOptions.map((option) => (
            <label key={option.value} className='column-value-filter-option'>
              <input
                type='checkbox'
                checked={selected.has(option.value)}
                disabled={isLoading}
                onChange={() => toggleValue(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))
        )}
      </div>
      <div className='column-value-filter-footer'>
        <button
          type='button'
          className='column-value-filter-confirm'
          disabled={isLoading || draftValues.length === 0}
          onClick={() => {
            onClose()
            onApply(draftValues)
          }}
        >
          OK
        </button>
        <button
          type='button'
          className='column-value-filter-cancel'
          disabled={isLoading}
          onClick={onClose}
        >
          キャンセル
        </button>
      </div>
    </div>
  )

  return createPortal(popup, document.body)
}

export default ColumnValueFilter
